import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Bot, Loader2, MessageSquare, Trash2, Search, Plus, Edit2, RefreshCw, Sparkles,
} from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('fr-FR'); } catch (e) { return '—'; }
}

export default function AdminChatbotModeration() {
  const { t } = useLanguage();
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
          <Bot className="w-7 h-7 text-blue-700 dark:text-blue-400" /> {t('admin.moderationTitle')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {t('admin.moderationSubtitle')}
        </p>
      </div>

      <Tabs defaultValue="conversations">
        <TabsList>
          <TabsTrigger value="conversations" data-testid="tab-conversations">
            <MessageSquare className="w-4 h-4 mr-1" /> Conversations
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom-responses">
            <Sparkles className="w-4 h-4 mr-1" /> Réponses personnalisées
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conversations">
          <ConversationsTab />
        </TabsContent>
        <TabsContent value="custom">
          <CustomResponsesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConversationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // { client_id, messages, client_name, client_email }
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/admin/chatbot/conversations', { params });
      setItems(Array.isArray(r.data) ? r.data : (r.data?.conversations || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openConversation = async (c) => {
    try {
      setDetailLoading(true);
      const cid = c.client_id || c.id;
      const r = await api.get(`/admin/chatbot/conversations/${cid}`);
      const data = r.data || {};
      setSelected({
        client_id: cid,
        client_name: c.client_name || data.client_name,
        client_email: c.client_email || data.client_email,
        messages: Array.isArray(data) ? data : (data.messages || data.history || []),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setDetailLoading(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/chatbot/conversations/${confirmDelete.client_id || confirmDelete.id}`);
      toast.success('Historique supprimé');
      setConfirmDelete(null);
      if (selected?.client_id === (confirmDelete.client_id || confirmDelete.id)) setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs uppercase text-slate-500 dark:text-slate-400">Rechercher</Label>
          <Input
            placeholder="Nom ou email client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            data-testid="input-conv-search"
          />
        </div>
        <Button onClick={load} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-conv-search">
          <Search className="w-4 h-4 mr-1" /> Rechercher
        </Button>
        <Button variant="outline" onClick={() => { setSearch(''); setTimeout(load, 0); }}>Reset</Button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Chargement...
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400 text-sm">
            Aucune conversation.
          </div>
        ) : (
          <div className="divide-y dark:divide-slate-800">
            {items.map((c) => {
              const cid = c.client_id || c.id;
              return (
                <div key={cid} className="p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                  <button
                    onClick={() => openConversation(c)}
                    className="flex-1 text-left"
                    data-testid={`row-conv-${cid}`}
                  >
                    <div className="font-semibold dark:text-white">
                      {c.client_name || 'Client'} <span className="text-xs text-slate-500 dark:text-slate-400">· {c.client_email || ''}</span>
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 line-clamp-1">
                      {c.last_message_preview || c.last_message || '—'}
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {c.message_count ?? 0} messages · {fmtDate(c.last_activity || c.updated_at)}
                    </div>
                  </button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setConfirmDelete(c)}
                    data-testid={`btn-conv-delete-${cid}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Conversation dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <MessageSquare className="w-5 h-5 text-blue-700 dark:text-blue-400" />
              Conversation — {selected?.client_name}
            </DialogTitle>
            <DialogDescription>{selected?.client_email}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center text-slate-500"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {(selected?.messages || []).length === 0 ? (
                <div className="text-center text-slate-500 dark:text-slate-400 text-sm py-6">Aucun message.</div>
              ) : (
                (selected?.messages || []).map((m, i) => {
                  const isBot = m.role === 'bot' || m.from === 'bot' || m.author === 'bot';
                  return (
                    <div key={m.id || i} className={`flex ${isBot ? '' : 'justify-end'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                        isBot
                          ? 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-sm'
                          : 'bg-blue-600 text-white rounded-tr-sm'
                      }`}>
                        <div className={`text-xs font-semibold mb-1 ${isBot ? 'text-slate-500 dark:text-slate-400' : 'text-blue-100'}`}>
                          {isBot ? 'Chatbot' : 'Client'} · {fmtDate(m.created_at || m.timestamp)}
                        </div>
                        <div className="whitespace-pre-line">{m.message || m.text || m.content}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer l'historique ?</DialogTitle>
            <DialogDescription>
              Toute la conversation chatbot de <strong>{confirmDelete?.client_name || confirmDelete?.client_email}</strong> sera supprimée.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="btn-conv-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomResponsesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | object (existing) | 'new'
  const [form, setForm] = useState({ question_pattern: '', response: '', priority: 0, enabled: true });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/chatbot/custom-responses');
      setItems(Array.isArray(r.data) ? r.data : (r.data?.responses || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ question_pattern: '', response: '', priority: 0, enabled: true });
    setEditing('new');
  };
  const openEdit = (it) => {
    setForm({
      question_pattern: it.question_pattern || '',
      response: it.response || '',
      priority: it.priority ?? 0,
      enabled: it.enabled !== false,
    });
    setEditing(it);
  };

  const save = async () => {
    if (!form.question_pattern.trim() || !form.response.trim()) {
      toast.error('Le pattern et la réponse sont requis');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        question_pattern: form.question_pattern.trim(),
        response: form.response.trim(),
        priority: Number(form.priority) || 0,
        enabled: form.enabled,
      };
      if (editing === 'new') {
        await api.post('/admin/chatbot/custom-responses', payload);
        toast.success('Réponse personnalisée créée');
      } else {
        const id = editing.id || editing._id;
        await api.put(`/admin/chatbot/custom-responses/${id}`, payload);
        toast.success('Réponse mise à jour');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const id = confirmDelete.id || confirmDelete._id;
      await api.delete(`/admin/chatbot/custom-responses/${id}`);
      toast.success('Supprimé');
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Les réponses personnalisées sont vérifiées <strong>avant</strong> la réponse IA automatique. Les patterns sont des expressions régulières (regex) testées contre le message client.</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600 dark:text-slate-300">{items.length} réponse{items.length > 1 ? 's' : ''} configurée{items.length > 1 ? 's' : ''}</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
          </Button>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="btn-custom-add">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400 text-sm">
            Aucune réponse personnalisée. Cliquez sur "Ajouter" pour en créer une.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase text-slate-600 dark:text-slate-300">
                <tr>
                  <th className="p-3 text-left">Pattern (regex)</th>
                  <th className="p-3 text-left">Réponse</th>
                  <th className="p-3 text-center">Priorité</th>
                  <th className="p-3 text-center">État</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-800">
                {items.map((it) => (
                  <tr key={it.id || it._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs text-slate-700 dark:text-slate-200 max-w-[220px] truncate">{it.question_pattern}</td>
                    <td className="p-3 text-sm text-slate-600 dark:text-slate-300 max-w-[400px] truncate">{it.response}</td>
                    <td className="p-3 text-center text-sm dark:text-slate-200">{it.priority ?? 0}</td>
                    <td className="p-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        it.enabled === false
                          ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      }`}>
                        {it.enabled === false ? 'Désactivé' : 'Actif'}
                      </span>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="mr-1" data-testid={`btn-custom-edit-${it.id || it._id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(it)} data-testid={`btn-custom-delete-${it.id || it._id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nouvelle réponse personnalisée' : 'Modifier la réponse'}</DialogTitle>
            <DialogDescription>
              Le pattern est une regex insensible à la casse. La priorité (élevée d'abord) détermine l'ordre d'évaluation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Pattern (regex)</Label>
              <Input
                value={form.question_pattern}
                onChange={(e) => setForm({ ...form, question_pattern: e.target.value })}
                placeholder="ex. (horaires?|ouvert|fermeture)"
                className="font-mono"
                data-testid="input-custom-pattern"
              />
            </div>
            <div>
              <Label>Réponse</Label>
              <Textarea
                rows={5}
                value={form.response}
                onChange={(e) => setForm({ ...form, response: e.target.value })}
                placeholder="Texte qui sera envoyé au client en cas de match..."
                data-testid="textarea-custom-response"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priorité</Label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  data-testid="input-custom-priority"
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    data-testid="input-custom-enabled"
                  />
                  Actif
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-custom-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cette réponse ?</DialogTitle>
            <DialogDescription>
              La réponse personnalisée pour le pattern <code>{confirmDelete?.question_pattern}</code> sera supprimée.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="btn-custom-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
